import { useState, useEffect, useRef } from "react";

/**
 * Configuration constants for Pi Browser detection
 */
const PI_DETECTION_CONFIG = {
  MAX_RETRIES: 3,
  DETECTION_TIMEOUT: 5000,
  INITIAL_DELAY: 100,
  RETRY_DELAY_MULTIPLIER: 500,
};

/**
 * Custom hook for detecting Pi Browser environment
 * 
 * This hook performs comprehensive detection to determine if the app is running
 * in the official Pi Browser vs a regular web browser with mock Pi SDK.
 * 
 * Detection Methods (in priority order):
 * 1. Production/Sandbox flags in Pi object
 * 2. getPiHostAppInfo() behavior and response
 * 3. User Agent string patterns
 * 4. Pi SDK method availability (fallback)
 * 
 * @returns {Object} Detection state
 * @returns {boolean|null} isPiBrowser - true if Pi Browser, false if not, null if detecting
 * @returns {boolean} isDetecting - true while detection is in progress
 * @returns {string|null} detectionMethod - Method used for successful detection
 * @returns {Function} getDetectionInfo - Returns detailed detection information
 * 
 * @example
 * const { isPiBrowser, isDetecting, detectionMethod } = usePiBrowserDetection();
 * 
 * if (isDetecting) {
 *   return <LoadingSpinner />;
 * }
 * 
 * if (isPiBrowser) {
 *   return <PiOnlyFeature />;
 * }
 */
const usePiBrowserDetection = () => {
  const [isPiBrowser, setIsPiBrowser] = useState(null);
  const [isDetecting, setIsDetecting] = useState(true);
  const [detectionMethod, setDetectionMethod] = useState(null);
  const timeoutRef = useRef(null);
  const retryCountRef = useRef(0);

  useEffect(() => {
    let cancelled = false;

    /**
     * Helper to race a promise against a timeout
     */
    const detectWithTimeout = (promise, timeoutMs = PI_DETECTION_CONFIG.DETECTION_TIMEOUT) => {
      const timeoutPromise = new Promise((resolve) =>
        setTimeout(() => resolve("timeout"), timeoutMs)
      );
      return Promise.race([promise, timeoutPromise]);
    };

    /**
     * Check if a value indicates a "true" flag
     */
    const isTrueFlag = (value) => value === true || value === 'true';

    /**
     * Search for Prod/Sandbox flags in multiple locations
     */
    const checkEnvironmentFlags = (piObject) => {
      let isProd = false;
      let isSandbox = false;

      const checkLocations = [
        piObject,
        Object.getPrototypeOf(piObject),
        piObject.constructor?.prototype,
        window.Pi,
      ].filter(Boolean);

      for (const location of checkLocations) {
        try {
          // Direct property check
          if ('Prod' in location && isTrueFlag(location.Prod)) {
            isProd = true;
          }
          if ('Sandbox' in location && isTrueFlag(location.Sandbox)) {
            isSandbox = true;
          }

          // Property descriptor check
          const prodDescriptor = Object.getOwnPropertyDescriptor(location, 'Prod');
          const sandboxDescriptor = Object.getOwnPropertyDescriptor(location, 'Sandbox');

          if (prodDescriptor) {
            const prodValue = prodDescriptor.get ? prodDescriptor.get.call(piObject) : prodDescriptor.value;
            if (isTrueFlag(prodValue)) isProd = true;
          }

          if (sandboxDescriptor) {
            const sandboxValue = sandboxDescriptor.get ? sandboxDescriptor.get.call(piObject) : sandboxDescriptor.value;
            if (isTrueFlag(sandboxValue)) isSandbox = true;
          }
        } catch (e) {
          // Silently continue checking other locations
        }
      }

      // Iterate through prototype chain
      try {
        let currentObj = piObject;
        while (currentObj && currentObj !== Object.prototype) {
          const allProps = Object.getOwnPropertyNames(currentObj);

          for (const prop of allProps) {
            if (prop === 'Prod' || prop === 'Sandbox') {
              try {
                const value = currentObj[prop];
                if (prop === 'Prod' && isTrueFlag(value)) isProd = true;
                if (prop === 'Sandbox' && isTrueFlag(value)) isSandbox = true;
              } catch (e) {
                // Continue checking
              }
            }
          }
          currentObj = Object.getPrototypeOf(currentObj);
        }
      } catch (e) {
        // Continue with detection
      }

      return { isProd, isSandbox };
    };

    /**
     * Check User Agent for Pi Browser patterns
     */
    const checkUserAgent = () => {
      const ua = navigator?.userAgent?.toLowerCase() || "";
      const piUserAgentPatterns = ['pibrowser', 'pi browser', 'pi-browser'];
      return piUserAgentPatterns.some(pattern => ua.includes(pattern));
    };

    /**
     * Main detection logic
     */
    const detectEnvironment = async () => {
      try {
        // Check if Pi SDK exists
        if (typeof window !== 'undefined' && window.Pi) {
          const piObject = window.Pi;

          // Method 1: Check for Prod/Sandbox flags
          const { isProd, isSandbox } = checkEnvironmentFlags(piObject);

          if (isProd || isSandbox) {
            if (!cancelled) {
              setIsPiBrowser(true);
              setDetectionMethod(isProd ? "prodFlag" : "sandboxFlag");
              return;
            }
          }

          // Method 2: Check getPiHostAppInfo behavior
          if (typeof window.Pi.getPiHostAppInfo === 'function') {
            try {
              const result = await detectWithTimeout(window.Pi.getPiHostAppInfo(), 3000);

              if (!cancelled && result !== "timeout") {
                // Standard Pi Browser response
                if (typeof result === 'object' && result.hostApp === "pi-browser") {
                  setIsPiBrowser(true);
                  setDetectionMethod("getPiHostAppInfo");
                  return;
                }

                // Pi Sandbox behavior: empty object indicates real Pi Sandbox
                if (typeof result === 'object' && Object.keys(result).length === 0) {
                  setIsPiBrowser(true);
                  setDetectionMethod("piSandboxBehavior");
                  return;
                }

                // Any other valid non-timeout result from Pi environment
                if (result !== null && result !== undefined) {
                  setIsPiBrowser(true);
                  setDetectionMethod("getPiHostAppInfoSuccess");
                  return;
                }
              }
            } catch (error) {
              console.warn('getPiHostAppInfo failed - likely mock Pi SDK');
            }
          }

          // Method 3: User Agent check
          if (checkUserAgent()) {
            if (!cancelled) {
              setIsPiBrowser(true);
              setDetectionMethod("userAgent");
              return;
            }
          }

          // Retry logic for timing issues
          if (retryCountRef.current < PI_DETECTION_CONFIG.MAX_RETRIES) {
            retryCountRef.current++;
            timeoutRef.current = setTimeout(() => {
              if (!cancelled) {
                detectEnvironment();
              }
            }, PI_DETECTION_CONFIG.RETRY_DELAY_MULTIPLIER * retryCountRef.current);
            return;
          }
        }

        // No Pi Browser indicators found
        if (!cancelled) {
          setIsPiBrowser(false);
          setDetectionMethod("default");
        }

      } catch (error) {
        console.error('Pi Browser detection error:', error);

        if (!cancelled) {
          setIsPiBrowser(false);
          setDetectionMethod("error");
        }
      } finally {
        if (!cancelled) {
          setIsDetecting(false);
        }
      }
    };

    // Add initial delay to ensure DOM and scripts are loaded
    const initialDelay = setTimeout(() => {
      if (!cancelled) {
        detectEnvironment();
      }
    }, PI_DETECTION_CONFIG.INITIAL_DELAY);

    return () => {
      cancelled = true;
      clearTimeout(initialDelay);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  /**
   * Get detailed detection information for debugging
   */
  const getDetectionInfo = () => ({
    isPiBrowser,
    isDetecting,
    detectionMethod,
    retryCount: retryCountRef.current,
    windowPiExists: typeof window !== 'undefined' && !!window.Pi,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'N/A',
    isLocalhost: typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1' ||
        window.location.hostname.includes('local'))
  });

  return {
    isPiBrowser,
    isDetecting,
    detectionMethod,
    getDetectionInfo
  };
};

export default usePiBrowserDetection;